// utils/eventEmitter.js
// A simple event emitter for better communication between components

export class EventEmitter {
  constructor() {
    this.events = {};
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name to subscribe to
   * @param {function} listener - Callback function to execute when event is emitted
   * @returns {function} - Unsubscribe function
   */
  on(event, listener) {
    if (!this.events[event]) {
      this.events[event] = [];
    }

    this.events[event].push(listener);

    // Return a function to unsubscribe
    return () => {
      this.off(event, listener);
    };
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {function} listener - The listener function to remove
   */
  off(event, listener) {
    if (!this.events[event]) {
      return;
    }

    this.events[event] = this.events[event].filter(
      existingListener => existingListener !== listener
    );
  }

  /**
   * Subscribe to an event and unsubscribe after it fires once
   * @param {string} event - Event name
   * @param {function} listener - The listener function
   * @returns {function} - Unsubscribe function
   */
  once(event, listener) {
    const onceListener = (...args) => {
      this.off(event, onceListener);
      listener(...args);
    };

    return this.on(event, onceListener);
  }

  /**
   * Emit an event with data
   * @param {string} event - Event name
   * @param {any} data - Data to pass to listeners
   */
  emit(event, data) {
    if (!this.events[event]) {
      return;
    }

    this.events[event].forEach(listener => {
      try {
        listener(data);
      } catch (error) {
        console.error(`Error in event listener for "${event}":`, error);
      }
    });
  }

  /**
   * Remove all listeners for an event
   * @param {string} event - Optional event name. If not provided, all events are cleared.
   */
  clear(event) {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

// Create a global event bus for app-wide events
export const globalEventBus = new EventEmitter();

// Predefined global event names
export const GLOBAL_EVENTS = {
  SYNC_STARTED: 'sync:started',
  SYNC_COMPLETED: 'sync:completed',
  SYNC_FAILED: 'sync:failed',
  DB_CHANGED: 'db:changed',
  DOWNLOAD_COMPLETED: 'download:completed',
  NETWORK_STATUS_CHANGED: 'network:status:changed',
  DROPDOWN_DATA_UPDATED: 'dropdown:data:updated'
};

export default EventEmitter;
